import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Sparkles, Mail, Lock, User, Users, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSession } from "@/App";
import BrandLogo from "@/components/BrandLogo";
import PasswordInput from "@/components/PasswordInput";

export default function AuthPage() {
  const [mode, setMode] = useState("login");
  const [searchParams] = useSearchParams();
  const referrerFromUrl = searchParams.get("ref") || "";
  const [form, setForm] = useState({
    name: "", email: "", password: "", family_name: "", invite_code: "", referrer_code: referrerFromUrl,
  });
  const [loading, setLoading] = useState(false);
  const [joinFamily, setJoinFamily] = useState(false);
  const navigate = useNavigate();
  const { login } = useSession();

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        const { data } = await api.post("/auth/login", { email: form.email, password: form.password });
        await login(data.token, data.user);
        toast.success(`Bentornato, ${data.user.name}!`);
        navigate("/app");
      } else {
        const payload = {
          name: form.name, email: form.email, password: form.password,
          family_name: joinFamily ? null : form.family_name,
          invite_code: joinFamily ? form.invite_code : null,
          referrer_code: form.referrer_code || null,
        };
        const { data } = await api.post("/auth/register", payload);
        await login(data.token, data.user);
        toast.success("Account creato!");
        navigate(joinFamily ? "/app" : "/onboarding");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-app">
      <div className="hidden md:flex md:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1511895426328-dc8714191300?w=1400&q=80"
          alt="Famiglia spensierata"
          className="absolute inset-0 w-full h-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-app/70 via-app/30 to-app" />
        <div className="relative z-10 p-12 flex flex-col justify-end text-primary-fg">
          <div className="flex items-center gap-3 mb-6">
            <BrandLogo size={48} />
            <div className="font-display text-2xl font-semibold">Family Brain</div>
          </div>
          <h1 className="font-display text-4xl lg:text-5xl font-semibold tracking-tight leading-tight max-w-md">
            La tua famiglia parla.<br />
            <span className="text-brand">Family Brain</span> organizza.
          </h1>
          <p className="text-secondary-fg text-base mt-4 max-w-md leading-relaxed">
            L&apos;AI che organizza la vita di famiglia. Automaticamente.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-10 md:py-0">
        <div className="w-full max-w-sm">
          <div className="md:hidden flex items-center gap-3 mb-8">
            <BrandLogo size={44} />
            <div>
              <div className="font-display font-semibold text-lg leading-none">Family Brain</div>
              <div className="text-xs text-tertiary-fg uppercase tracking-widest mt-0.5">Famiglia</div>
            </div>
          </div>

          <h2 className="font-display text-3xl font-semibold tracking-tight">
            {mode === "login" ? "Bentornato" : "Crea il tuo account"}
          </h2>
          <p className="text-secondary-fg text-sm mt-2 mb-8">
            {mode === "login" ? "Accedi alla memoria della tua famiglia." : "Inizia la tua memoria familiare."}
          </p>

          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <div className="relative">
                <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-tertiary-fg" />
                <input
                  data-testid="auth-name-input"
                  className="input-warm pl-10"
                  placeholder="Il tuo nome"
                  value={form.name}
                  onChange={handle("name")}
                  required
                />
              </div>
            )}
            <div className="relative">
              <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-tertiary-fg" />
              <input
                data-testid="auth-email-input"
                type="email"
                className="input-warm pl-10"
                placeholder="email@esempio.it"
                value={form.email}
                onChange={handle("email")}
                required
              />
            </div>
            <PasswordInput
              data-testid="auth-password-input"
              placeholder="Password"
              value={form.password}
              onChange={handle("password")}
              required
              minLength={6}
            />

            {mode === "register" && (
              <>
                <div className="flex gap-2 text-xs pt-1">
                  <button
                    type="button"
                    data-testid="auth-toggle-newfam"
                    onClick={() => setJoinFamily(false)}
                    className={`flex-1 px-3 py-2 rounded-xl border ${!joinFamily ? "bg-elevated border-brand text-primary-fg" : "border-subtle text-tertiary-fg"}`}
                  >
                    Crea famiglia
                  </button>
                  <button
                    type="button"
                    data-testid="auth-toggle-join"
                    onClick={() => setJoinFamily(true)}
                    className={`flex-1 px-3 py-2 rounded-xl border ${joinFamily ? "bg-elevated border-brand text-primary-fg" : "border-subtle text-tertiary-fg"}`}
                  >
                    Unisciti
                  </button>
                </div>
                {!joinFamily ? (
                  <div className="relative">
                    <Users className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-tertiary-fg" />
                    <input
                      data-testid="auth-family-name"
                      className="input-warm pl-10"
                      placeholder="Nome della famiglia (es. Famiglia Rossi)"
                      value={form.family_name}
                      onChange={handle("family_name")}
                    />
                  </div>
                ) : (
                  <div className="relative">
                    <Users className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-tertiary-fg" />
                    <input
                      data-testid="auth-invite-code"
                      className="input-warm pl-10 uppercase tracking-widest"
                      placeholder="Codice invito"
                      value={form.invite_code}
                      onChange={handle("invite_code")}
                      required
                    />
                  </div>
                )}
                {form.referrer_code && (
                  <div className="text-xs text-sage flex items-center gap-1 px-1">
                    🎁 Codice referral applicato: <span className="font-mono font-semibold">{form.referrer_code}</span>
                  </div>
                )}
              </>
            )}

            <button
              type="submit"
              data-testid="auth-submit-button"
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center gap-2 mt-2"
            >
              {loading ? "Attendi..." : mode === "login" ? "Accedi" : "Crea account"}
              {!loading && <ArrowRight className="w-4 h-4" />}
            </button>
          </form>

          {mode === "login" && (
            <div className="text-center mt-4">
              <a
                href="/forgot-password"
                data-testid="auth-forgot-password-link"
                className="text-sm text-tertiary-fg hover:text-brand hover:underline"
              >
                Password dimenticata?
              </a>
            </div>
          )}

          <div className="text-center mt-6 text-sm text-secondary-fg">
            {mode === "login" ? "Non hai un account?" : "Hai già un account?"}{" "}
            <button
              data-testid="auth-toggle-mode"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="text-brand hover:underline font-medium"
            >
              {mode === "login" ? "Registrati" : "Accedi"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
